import { StateCreator } from "zustand";

import type { ViewMode } from "@/components/organisms/DataView";
import { Values } from "@/constants";
import {
  ActiveTabNodeIdType,
  AppViewsType,
  AppViewTabModeType,
  CoreSliceType,
  CoreStateType,
  GlobalStoreStateType,
  NEW_THREAD_THREAD_ID,
  OpenThreadTabType,
  ThreadIdType,
  ThreadTabActivityType,
} from "@/state/@types";
import {
  DEFAULT_OPEN_THREAD_TABS,
  ensureOpenThreadTabs,
  reorderThreadTabs,
  tabIdToThreadNodeId,
  threadNodeIdToTabId,
} from "@/state/threadTabs";

export const coreInitialState: CoreStateType = {
  activeAppView: "triage",
  activeThreadNodeId: null,
  openThreadTabs: DEFAULT_OPEN_THREAD_TABS,
  threadTabActivity: {},
  activeEntityIdentifiers: [],
  activePanelNodeIds: {},
  activePanelNodeId: null,
  activePanelAutofocus: false,
  panelHeaderExtras: null,
  activeNodePanelGroupIds: {},
  nodeDetailLayoutModes: {},
  isTriageOneAtATime: false,
  hiddenTriageRowParts: [],
  isTriageMirrored: false,
  activeTabNodeIds: {} as ActiveTabNodeIdType,
  activeTab: "all" as AppViewTabModeType,
  dataViewModes: {
    "financial-accounts": "card",
  } as Record<string, ViewMode>,
  entities: [],
  timeSeriesFilter: {
    startDate: null,
    endDate: null,
    frequency: null,
  },
  isThreadListCollapsed: false,
  isMutating: null,
  isNodePanelOpen: false,
  isThreadPanelOpen: false,
  threadPanelFocusRequestId: 0,
  threadPanelPendingFiles: [],
  threadReviewRequestNodeId: null,
  isPanelResizing: false,
  isSidebarMinimized: false,
  nodePanelSize: null,
  threadPanelWidth: null,
  isSidebarResizing: false,
  isClientOutdated: false,
  onSetActiveTab: null,
  previousAppView: null,
  queuedPanelNodeIds: [],
  reviewableDialogNodeIds: [],
  scopedEntityIdentifier: null,
  externalGrantIdentifiers: [],
  snackbarMessage: "",
  styleTheme: "rounded",
  userIdentifier: null,
};

export const createCoreSlice: StateCreator<
  GlobalStoreStateType,
  [],
  [],
  CoreSliceType
> = (set, get) => ({
  ...coreInitialState,
  getEntityIdentifier: () => {
    if (get().scopedEntityIdentifier) {
      return get().scopedEntityIdentifier;
    }
    return get().activeEntityIdentifiers?.[0] || null;
  },
  popNodeIdFromQueue: () => {
    const view = get().activeAppView;
    const activePanelNodeId = get().activePanelNodeId;
    const queuedPanelNodeIds = get().queuedPanelNodeIds;
    const newActivePanelNodeId = queuedPanelNodeIds[0];
    let remainingQueuedPanelNodeIds = queuedPanelNodeIds.slice(1);
    remainingQueuedPanelNodeIds = remainingQueuedPanelNodeIds.filter(
      (nodeId) =>
        nodeId !== activePanelNodeId && nodeId !== newActivePanelNodeId,
    );
    set({
      activePanelNodeIds: {
        ...get().activePanelNodeIds,
        [view]: newActivePanelNodeId || null,
      },
      activePanelNodeId: newActivePanelNodeId || null,
      isNodePanelOpen: Boolean(newActivePanelNodeId),
      queuedPanelNodeIds: remainingQueuedPanelNodeIds,
      activePanelAutofocus: false,
    });
  },
  setActiveAppView: (view: AppViewsType) => {
    let newView: AppViewsType = view;
    let newTab: AppViewTabModeType | undefined;

    const alias = Values.VIEW_ALIAS_MAP[view as string];
    if (alias) {
      newView = alias.view;
      newTab = alias.tab;
    } else {
      newTab = Values.APP_VIEW_TAB_MODE_MAP[newView]?.values?.[0];
    }

    const defaultTabForNewView =
      Values.APP_VIEW_TAB_MODE_MAP[newView]?.values?.[0];
    const targetTab =
      newTab &&
      Values.APP_VIEW_TAB_MODE_MAP[newView]?.values?.includes(
        newTab as AppViewTabModeType,
      )
        ? newTab
        : defaultTabForNewView;

    // Re-mirror ``activePanelNodeId`` to the new view's per-view value so the
    // Node panel + downstream consumers see the right id immediately.
    const nextNodeId = get().activePanelNodeIds[newView] ?? null;

    set({
      previousAppView: get().activeAppView,
      activeAppView: newView,
      activeTab: targetTab,
      activePanelNodeId: nextNodeId,
    });

    const onSetActiveTabCallback = get().onSetActiveTab;
    if (onSetActiveTabCallback && targetTab) {
      onSetActiveTabCallback(targetTab);
    }
  },
  setActiveThreadNodeId: (nodeId) => {
    get().openThreadTab(nodeId, { activate: true, openPanel: false });
  },
  openThreadTab: (threadNodeId, options = {}) => {
    const {
      label,
      activate = true,
      openPanel = true,
      preview = false,
      review = false,
    } = options;
    if (review) set({ threadReviewRequestNodeId: threadNodeId });
    const tabId = threadNodeIdToTabId(threadNodeId);
    const tabs = get().openThreadTabs;
    const existingIndex = tabs.findIndex((t) => t.id === tabId);
    // A preview takes the current preview tab's place instead of adding one.
    const replacedIndex =
      existingIndex < 0 && preview ? tabs.findIndex((t) => t.isPreview) : -1;
    const newTab: OpenThreadTabType = {
      id: tabId,
      ...(label != null ? { label } : {}),
      ...(preview ? { isPreview: true } : {}),
    };
    let nextTabs: OpenThreadTabType[];
    if (existingIndex >= 0) {
      nextTabs = tabs.map((t, i) =>
        i === existingIndex ? { ...t, ...(label != null ? { label } : {}) } : t,
      );
    } else if (replacedIndex >= 0) {
      nextTabs = tabs.map((t, i) => (i === replacedIndex ? newTab : t));
    } else {
      nextTabs = [...tabs, newTab];
    }
    // Moving off an untouched "New thread" tab closes it: with nothing typed
    // there is nothing to come back to.
    const { activeThreadNodeId, threadDrafts } = get();
    if (
      activate &&
      tabId !== NEW_THREAD_THREAD_ID &&
      activeThreadNodeId === null &&
      !threadDrafts[NEW_THREAD_THREAD_ID]
    ) {
      nextTabs = nextTabs.filter((t) => t.id !== NEW_THREAD_THREAD_ID);
    }
    const threadTabActivity = { ...get().threadTabActivity };
    if (replacedIndex >= 0) {
      delete threadTabActivity[tabs[replacedIndex].id];
    }
    set({
      openThreadTabs: nextTabs,
      threadTabActivity,
      ...(activate ? { activeThreadNodeId: tabIdToThreadNodeId(tabId) } : {}),
      ...(openPanel ? { isThreadPanelOpen: true } : {}),
    });
  },
  selectThreadTab: (tabId) => {
    get().openThreadTab(tabIdToThreadNodeId(tabId), {
      activate: true,
      openPanel: false,
    });
  },
  closeThreadTab: (tabId) => {
    get().closeThreadTabs([tabId]);
  },
  closeThreadTabs: (tabIds) => {
    const idsToClose = new Set(tabIds);
    if (idsToClose.size === 0) return;

    const prevTabs = get().openThreadTabs;
    const activeTabId = threadNodeIdToTabId(get().activeThreadNodeId);
    const isClosingActive = idsToClose.has(activeTabId);
    const remaining = prevTabs.filter((t) => !idsToClose.has(t.id));
    const tabsChanged = remaining.length !== prevTabs.length;

    if (!tabsChanged && !isClosingActive) return;

    const remainingActivity = { ...get().threadTabActivity };
    // A closed tab's unsent draft goes with it.
    const threadDrafts = { ...get().threadDrafts };
    for (const id of idsToClose) {
      delete remainingActivity[id as ThreadIdType];
      delete threadDrafts[id as ThreadIdType];
    }

    if (!tabsChanged && isClosingActive) {
      set({
        activeThreadNodeId: null,
        threadTabActivity: remainingActivity,
        threadDrafts,
      });
      return;
    }

    if (remaining.length === 0) {
      set({
        openThreadTabs: [...DEFAULT_OPEN_THREAD_TABS],
        activeThreadNodeId: null,
        threadTabActivity: remainingActivity,
        threadDrafts,
      });
      return;
    }

    if (isClosingActive) {
      const closedIndex = prevTabs.findIndex((t) => t.id === activeTabId);
      const nextIndex = Math.min(
        closedIndex >= 0 ? closedIndex : 0,
        remaining.length - 1,
      );
      const nextTab = remaining[nextIndex];
      set({
        openThreadTabs: remaining,
        activeThreadNodeId: tabIdToThreadNodeId(nextTab.id),
        threadTabActivity: remainingActivity,
        threadDrafts,
      });
      return;
    }

    set({
      openThreadTabs: remaining,
      threadTabActivity: remainingActivity,
      threadDrafts,
    });
  },
  reorderOpenThreadTabs: (orderedTabIds) => {
    const tabs = get().openThreadTabs;
    const next = reorderThreadTabs(tabs, orderedTabIds);
    if (next !== tabs) {
      set({ openThreadTabs: next });
    }
  },
  openNewThreadTab: () => {
    const tabs = get().openThreadTabs;
    if (tabs.some((t) => t.id === NEW_THREAD_THREAD_ID)) {
      get().selectThreadTab(NEW_THREAD_THREAD_ID);
      return;
    }
    get().openThreadTab(null, { activate: true, openPanel: false });
  },
  openNewThreadWithFiles: (files) => {
    get().openNewThreadTab();
    set({ threadPanelPendingFiles: files });
    get().openThreadPanel({ autofocus: true });
  },
  promoteNewThreadTab: (threadId, label) => {
    const tabs = get().openThreadTabs;
    const withoutDraft = tabs.filter((t) => t.id !== NEW_THREAD_THREAD_ID);
    const existing = withoutDraft.find((t) => t.id === threadId);
    let nextTabs: OpenThreadTabType[];
    if (existing) {
      nextTabs = withoutDraft.map((t) =>
        t.id === threadId ? { ...t, ...(label != null ? { label } : {}) } : t,
      );
    } else {
      const draft = tabs.find((t) => t.id === NEW_THREAD_THREAD_ID);
      nextTabs = [
        ...withoutDraft,
        {
          id: threadId,
          label: label ?? draft?.label,
        },
      ];
    }
    const activity = { ...get().threadTabActivity };
    const draftActivity = activity[NEW_THREAD_THREAD_ID];
    delete activity[NEW_THREAD_THREAD_ID as ThreadIdType];
    if (draftActivity) {
      activity[threadId] = {
        ...(activity[threadId] ?? {
          isStreaming: false,
          pendingDeltaCount: 0,
        }),
        ...draftActivity,
      };
    }
    set({
      openThreadTabs: nextTabs,
      activeThreadNodeId: threadId,
      threadTabActivity: activity,
    });
  },
  setThreadTabActivity: (tabId, activity) => {
    const prev = get().threadTabActivity[tabId] ?? {
      isStreaming: false,
      pendingDeltaCount: 0,
    };
    const next: ThreadTabActivityType = { ...prev, ...activity };
    if (!next.isStreaming && next.pendingDeltaCount === 0) {
      const { [tabId]: _removed, ...rest } = get().threadTabActivity;
      set({ threadTabActivity: rest });
      return;
    }
    set({
      threadTabActivity: {
        ...get().threadTabActivity,
        [tabId]: next,
      },
    });
  },
  setThreadTabLabel: (tabId, label) => {
    const tabs = get().openThreadTabs;
    if (!tabs.some((t) => t.id === tabId)) return;
    set({
      openThreadTabs: tabs.map((t) => (t.id === tabId ? { ...t, label } : t)),
    });
  },
  pinThreadTab: (tabId) => {
    const tabs = get().openThreadTabs;
    if (!tabs.some((t) => t.id === tabId && t.isPreview)) return;
    set({
      openThreadTabs: tabs.map((t) =>
        t.id === tabId ? { ...t, isPreview: false } : t,
      ),
    });
  },
  setActivePanelNodeId: (
    nodeId: string | null,
    options: { queue?: boolean; autofocus?: boolean } = {},
  ) => {
    // ``autofocus`` defaults to ``true`` for explicit "edit this object"
    // entry points; navigation entry points opt out via ``{ autofocus: false }``.
    const { queue = false, autofocus = true } = options;
    const view = get().activeAppView;
    const previousActivePanelNodeId = get().activePanelNodeId;

    if (nodeId === previousActivePanelNodeId) {
      // The id is unchanged — typically the panel is already showing this
      // node — but the caller still expressed an explicit intent. Republish
      // ``activePanelAutofocus`` so downstream effects re-fire.
      set({ activePanelAutofocus: autofocus });
      return;
    }

    const nextActivePanelNodeIds = {
      ...get().activePanelNodeIds,
      [view]: nodeId,
    };

    // Picking a record points the panel at it and leaves it as it was: it
    // used to open beside an open thread whenever the record changed, so a
    // click on a cell reopened a panel the user had just shut. Only
    // ``openNodePanel`` opens it.
    if (queue) {
      const currentQueue = get().queuedPanelNodeIds;
      let updatedQueue = currentQueue.filter((id) => id !== nodeId);

      if (previousActivePanelNodeId) {
        if (!updatedQueue.includes(previousActivePanelNodeId)) {
          updatedQueue = [previousActivePanelNodeId, ...updatedQueue];
        } else {
          updatedQueue = [
            previousActivePanelNodeId,
            ...updatedQueue.filter((id) => id !== previousActivePanelNodeId),
          ];
        }
      }
      set({
        activePanelNodeIds: nextActivePanelNodeIds,
        activePanelNodeId: nodeId,
        queuedPanelNodeIds: updatedQueue,
        activePanelAutofocus: autofocus,
      });
    } else {
      set({
        activePanelNodeIds: nextActivePanelNodeIds,
        activePanelNodeId: nodeId,
        queuedPanelNodeIds: [],
        activePanelAutofocus: autofocus,
      });
    }
  },
  openNodePanel: (nodeId, options = {}) => {
    if (nodeId !== undefined) {
      get().setActivePanelNodeId(nodeId, options);
    }
    set({ isNodePanelOpen: true });
  },
  openThreadPanel: (options = {}) => {
    const { autofocus = false } = options;
    set((state) => ({
      isThreadPanelOpen: true,
      ...(autofocus
        ? { threadPanelFocusRequestId: state.threadPanelFocusRequestId + 1 }
        : {}),
    }));
  },
  closeAllPanels: () =>
    set({ isNodePanelOpen: false, isThreadPanelOpen: false }),
  setActiveTabNodeIds: (tabNodeIds: ActiveTabNodeIdType) => {
    set({ activeTabNodeIds: tabNodeIds });
  },
  setActiveTab: (tab: AppViewTabModeType) => {
    set({ activeTab: tab });
    const onSetActiveTabCallback = get().onSetActiveTab;
    if (onSetActiveTabCallback) {
      onSetActiveTabCallback(tab);
    }
  },
  setDataViewMode: (mode: ViewMode, view?: AppViewsType) => {
    const activeView = view ?? get().activeAppView;
    set({
      dataViewModes: {
        ...get().dataViewModes,
        [activeView]: mode,
      },
    });
  },
  setTimeSeriesFilter: (filters) => set({ timeSeriesFilter: filters }),
  setIsMutating: (isMutating) => set({ isMutating: isMutating }),
  setIsThreadListCollapsed: (isThreadListCollapsed: boolean) =>
    set({ isThreadListCollapsed: isThreadListCollapsed }),
  setIsNodePanelOpen: (isNodePanelOpen: boolean) => set({ isNodePanelOpen }),
  setIsThreadPanelOpen: (isThreadPanelOpen: boolean) =>
    set({ isThreadPanelOpen }),
  setIsPanelResizing: (isPanelResizing: boolean) =>
    set({ isPanelResizing: isPanelResizing }),
  setIsSidebarMinimized: (isSidebarMinimized: boolean) =>
    set({ isSidebarMinimized: isSidebarMinimized }),
  setNodePanelSize: (nodePanelSize: number) => set({ nodePanelSize }),
  setThreadPanelWidth: (threadPanelWidth: number) => set({ threadPanelWidth }),
  setIsSidebarResizing: (isSidebarResizing: boolean) =>
    set({ isSidebarResizing: isSidebarResizing }),
  setSnackbarMessage: (message: string) => set({ snackbarMessage: message }),
  setStyleTheme: (styleTheme) => set({ styleTheme }),
  setPanelHeaderExtras: (extras) => set({ panelHeaderExtras: extras }),
  setActiveNodePanelGroupId: (typename, groupId) =>
    set((state) => {
      if (state.activeNodePanelGroupIds[typename] === groupId) {
        return state;
      }
      return {
        activeNodePanelGroupIds: {
          ...state.activeNodePanelGroupIds,
          [typename]: groupId,
        },
      };
    }),
  setNodeDetailLayoutMode: (typename, mode) =>
    set((state) => ({
      nodeDetailLayoutModes: {
        ...state.nodeDetailLayoutModes,
        [typename]: mode,
      },
    })),
  setTriageOneAtATime: (isTriageOneAtATime) => set({ isTriageOneAtATime }),
  setHiddenTriageRowParts: (hiddenTriageRowParts) =>
    set({ hiddenTriageRowParts }),
  setTriageMirrored: (isTriageMirrored) => set({ isTriageMirrored }),
});
